import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, type AvatarProps as CoreAvatarProps } from '@tyro/core';

const CenterBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '50%',
}));

export interface StudentAvatarPictureProps {
  name: string;
  src?: string | null | undefined;
  isPriorityStudent: boolean;
  hasSupportPlan: boolean;
  hasMedical: boolean;
  AvatarProps?: CoreAvatarProps;
  avatarBackgroundColor?: string;
  size?: number;
  person?: CoreAvatarProps['person'];
}

const removedColors = [
  'green.500',
  'green.600',
  'emerald.500',
  'emerald.600',
] as CoreAvatarProps['removedColors'];

export function StudentAvatarPicture({
  name,
  src,
  person,
  isPriorityStudent,
  hasSupportPlan,
  hasMedical,
  AvatarProps,
  avatarBackgroundColor,
  size = 42,
}: StudentAvatarPictureProps) {
  const { palette } = useTheme();

  const priorityPaddingAdjustment = size > 60 ? 10 : 8;

  return (
    <Badge
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{
        '& .MuiBadge-badge': {
          boxShadow: `0 0 0 2px ${
            avatarBackgroundColor ?? palette.background.paper
          }`,
          backgroundColor: palette.blue[500],
          transform: 'scale(1) translate(40%, -40%)',
        },
      }}
      overlap="circular"
      variant="dot"
      badgeContent={hasSupportPlan ? 1 : 0}
    >
      <CenterBox
        sx={{
          ...(isPriorityStudent && {
            background: `linear-gradient(${palette.indigo[400]}, ${palette.indigo[600]})`,
          }),
          overflow: 'hidden',
          width: size,
          height: size,
          position: 'relative',
        }}
      >
        <CenterBox
          sx={{
            ...(isPriorityStudent && {
              backgroundColor:
                avatarBackgroundColor ?? palette.background.paper,
            }),
            width: isPriorityStudent ? size - 4 : size,
            height: isPriorityStudent ? size - 4 : size,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '50%',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Avatar
            name={name}
            src={src}
            size={isPriorityStudent ? size - priorityPaddingAdjustment : size}
            {...AvatarProps}
            person={person}
            removedColors={removedColors}
            sx={{
              fontSize: size ? Math.ceil(size * 0.35) : undefined,
              border: 0,
            }}
          />
          {hasMedical && (
            <Box
              role="presentation"
              sx={{
                position: 'absolute',
                width: isPriorityStudent
                  ? size - priorityPaddingAdjustment
                  : size,
                height: isPriorityStudent
                  ? size - priorityPaddingAdjustment
                  : size,
                borderRadius: '50%',
                background: `conic-gradient(transparent 0% 47%, ${palette.green[400]} 55% 70%, transparent 78% 100%)`,
                mask: 'radial-gradient(circle at 50% 50%, transparent 0% 50%, black 50% 100%)',
                pointerEvents: 'none',
              }}
            />
          )}
        </CenterBox>
      </CenterBox>
    </Badge>
  );
}
